import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import RecordTable from "components/Records/RecordTable";
import ListPagination from "components/Records/ListPagination";
import RelativeTime from "components/RelativeTime";
import { useAuditLogs } from "hooks/useAuditLogs";
import { categoryBadgeClass } from "config/auditLogConfig";
import { ScrollText } from "lucide-react";

/**
 * The company-scoped view of the change log.
 *
 * It used to hand-roll its own fetch, its own card, its own statusBadgeClass
 * and its own categoryBadgeClass — a third copy of each, which is why this tab
 * and /settings/audit drifted apart. It now shares the hook and the table, so
 * a column added on one surface is a column on both.
 */
export default function ActivityTab({ companyId }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-createdAt");

  const { logs, pagination, loading, error, refetch } = useAuditLogs({
    companyId,
    page,
    limit,
    sort,
  });

  const rows = Array.isArray(logs) ? logs : [];

  // Narrower than the full list: the company is already the page, so there is
  // no Client dimension to carry and no filter bar to drive.
  const columns = useMemo(
    () => [
      {
        key: "title",
        header: "Change",
        width: "34%",
        cell: (log) => (
          <span className="font-medium">
            {log.title || log.entityName || "—"}
          </span>
        ),
      },
      {
        key: "changeType",
        header: "Type",
        width: "18%",
        sortKey: "changeType",
        cell: (log) =>
          log.changeType ? (
            <span className="badge badge-muted">{log.changeType}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "changeCategory",
        header: "Category",
        width: "12%",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell",
        cell: (log) =>
          log.changeCategory ? (
            <span className={`badge ${categoryBadgeClass(log.changeCategory)}`}>
              {log.changeCategory}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "description",
        header: "Detail",
        width: "20%",
        headerClassName: "hidden xl:table-cell",
        className: "hidden xl:table-cell text-muted-foreground",
        cell: (log) => log.change?.description || "—",
      },
      {
        key: "implementer",
        header: "By",
        width: "10%",
        cell: (log) => log.implementer?.name || "—",
      },
      {
        key: "createdAt",
        header: "When",
        width: "10%",
        sortKey: "createdAt",
        defaultSortDir: "desc",
        className: "text-muted-foreground",
        cell: (log) => <RelativeTime value={log.createdAt} />,
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <RecordTable
        columns={columns}
        rows={rows}
        rowKey={(log) => log.logId || log._id}
        onRowClick={(log) => log.logId && navigate(`/audit/${log.logId}`)}
        loading={loading}
        error={error}
        onRetry={refetch}
        sort={sort}
        onSortChange={(next) => {
          setSort(next);
          setPage(1);
        }}
        empty={{
          icon: ScrollText,
          heading: "No activity recorded",
          description: "Changes to this company are recorded here as they happen.",
          errorText: "Failed to load activity.",
        }}
      />

      {pagination.totalPages > 1 && (
        <ListPagination
          page={pagination.page || page}
          totalPages={pagination.totalPages || 0}
          total={pagination.total || 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}
    </div>
  );
}
